import React, { useEffect, useMemo, useState } from 'react';
import {
  Box,
  Card,
  CardContent,
  Typography,
  Button,
  Grid,
  Chip,
  LinearProgress,
  Avatar,
  Table,
  TableBody,
  TableCell,
  TableContainer,
  TableHead,
  TableRow,
  Paper,
  IconButton,
  Menu,
  MenuItem,
  Dialog,
  DialogTitle,
  DialogContent,
  DialogActions,
  TextField,
  FormControl,
  InputLabel,
  Select,
  MenuItem as SelectMenuItem,
} from '@mui/material';
import {
  Add as AddIcon,
  Campaign as CampaignIcon,
  Visibility as ViewsIcon,
  TouchApp as MouseIcon,
  TrendingUp as ConversionIcon,
  MoreVert as MoreIcon,
  PlayArrow as PlayIcon,
  Pause as PauseIcon,
  Edit as EditIcon,
  Delete as DeleteIcon,
  AutoAwesome as AIIcon,
} from '@mui/icons-material';
import api from '../../../utils/apiConfig';
import { useLocation } from '../../../contexts/LocationContext';

const Campaigns = () => {
  const [selectedCampaign, setSelectedCampaign] = useState(null);
  const [detailsOpen, setDetailsOpen] = useState(false);
  const [createOpen, setCreateOpen] = useState(false);
  const [anchorEl, setAnchorEl] = useState(null);
  const [campaigns, setCampaigns] = useState([]);
  const [loading, setLoading] = useState(false);
  const [error, setError] = useState(null);
  const [pagination, setPagination] = useState({ currentPage: 1, totalPages: 1 });
  const [campaignStats, setCampaignStats] = useState(null);
  const { selectedLocation } = useLocation();

  // AI Campaign Creator state
  const [aiOpen, setAiOpen] = useState(false);
  const [aiBrief, setAiBrief] = useState('');
  const [aiObjective, setAiObjective] = useState('');
  const [aiAudience, setAiAudience] = useState('');
  const [aiBudget, setAiBudget] = useState('');
  const [aiDurationDays, setAiDurationDays] = useState(30);
  const [aiLoading, setAiLoading] = useState(false);
  const [aiError, setAiError] = useState(null);
  const [aiSuggestion, setAiSuggestion] = useState(null);

  const normalizedCampaigns = useMemo(() => {
    return campaigns.map((c) => {
      // Normalize possible backend field names/casing
      const id = c.id || c._id || c.campaignId;
      const statusRaw = c.status || c.STATUS;
      const typeRaw = c.type || c.TYPE;
      const name = c.name;
      const description = c.description || c.details?.description || '';
      const budgetTotal = c.budget?.total ?? c.budget ?? 0;
      const spent = c.budget?.spent ?? c.spent ?? 0;
      const impressions = c.metrics?.impressions ?? c.impressions ?? 0;
      const clicks = c.metrics?.clicks ?? c.clicks ?? 0;
      const conversions = c.metrics?.conversions ?? c.conversions ?? 0;
      const revenue = c.metrics?.revenue ?? c.revenue ?? 0;
      const startDate = c.schedule?.startDate || c.startDate || '';
      const endDate = c.schedule?.endDate || c.endDate || '';

      // Map backend enums to display strings
      const statusMap = {
        draft: 'Draft',
        scheduled: 'Scheduled',
        active: 'Active',
        paused: 'Paused',
        completed: 'Completed',
        cancelled: 'Cancelled',
        DRAFT: 'Draft',
        SCHEDULED: 'Scheduled',
        ACTIVE: 'Active',
        PAUSED: 'Paused',
        COMPLETED: 'Completed',
        CANCELLED: 'Cancelled',
      };
      const typeMap = {
        email: 'Email Marketing',
        sms: 'SMS',
        social_media: 'Social Media',
        print: 'Print',
        radio: 'Radio',
        tv: 'TV',
        online_ads: 'Online Ads',
        direct_mail: 'Direct Mail',
        event: 'Event',
        other: 'Other',
      };

      const status = statusMap[statusRaw] || (typeof statusRaw === 'string' ? statusRaw : 'Draft');
      const type = typeMap[typeRaw] || (typeof typeRaw === 'string' ? typeRaw : 'Other');

      return {
        id,
        name,
        type,
        status,
        budget: Number(budgetTotal) || 0,
        spent: Number(spent) || 0,
        impressions: Number(impressions) || 0,
        clicks: Number(clicks) || 0,
        conversions: Number(conversions) || 0,
        revenue: Number(revenue) || 0,
        startDate,
        endDate,
        description,
      };
    });
  }, [campaigns]);

  const stats = useMemo(() => {
    const totals = campaignStats || {
      totalCampaigns: normalizedCampaigns.length,
      totalImpressions: normalizedCampaigns.reduce((a, c) => a + (c.impressions || 0), 0),
      totalClicks: normalizedCampaigns.reduce((a, c) => a + (c.clicks || 0), 0),
      totalRevenue: normalizedCampaigns.reduce((a, c) => a + (c.revenue || 0), 0),
    };
    return [
      {
        title: 'Total Campaigns',
        value: (totals.totalCampaigns ?? 0).toString(),
        icon: <CampaignIcon sx={{ fontSize: 40, color: 'primary.main' }} />,
      },
      {
        title: 'Total Impressions',
        value: (totals.totalImpressions ?? 0).toLocaleString(),
        icon: <ViewsIcon sx={{ fontSize: 40, color: 'info.main' }} />,
      },
      {
        title: 'Total Clicks',
        value: (totals.totalClicks ?? 0).toLocaleString(),
        icon: <MouseIcon sx={{ fontSize: 40, color: 'warning.main' }} />,
      },
      {
        title: 'Total Revenue',
        value: `$${(totals.totalRevenue ?? 0).toLocaleString()}`,
        icon: <ConversionIcon sx={{ fontSize: 40, color: 'success.main' }} />,
      },
    ];
  }, [normalizedCampaigns, campaignStats]);

  const fetchCampaignsPage = async (locationId, page = 1, limit = 100) => {
    const { data } = await api.get('/marketing/campaigns', {
      params: {
        locationId,
        page,
        limit,
        sortBy: 'createdAt',
        sortOrder: 'desc',
      },
    });
    return data?.data || { campaigns: [], pagination: { currentPage: page, totalPages: 1 } };
  };

  const loadCampaigns = async () => {
    if (!selectedLocation?._id) return;
    setLoading(true);
    setError(null);
    try {
      const locationId = selectedLocation._id; // Backend expects UUID; using _id currently
      let all = [];
      let page = 1;
      const maxPages = 10;
      let totalPages = 1;

      do {
        const res = await fetchCampaignsPage(locationId, page, 100);
        all = all.concat(res.campaigns || []);
        totalPages = res.pagination?.totalPages || 1;
        page += 1;
      } while (page <= totalPages && page <= maxPages);

      setCampaigns(all);
      setPagination({ currentPage: Math.min(page - 1, totalPages), totalPages });
    } catch (e) {
      console.error('Failed to load campaigns:', e);
      setError(e?.response?.data?.message || 'Failed to load campaigns');
    } finally {
      setLoading(false);
    }
  };

  const loadStats = async () => {
    if (!selectedLocation?._id) return;
    try {
      const locationId = selectedLocation._id;
      const { data } = await api.get(`/marketing/campaigns/stats/${locationId}`);
      if (data?.success) {
        const s = data.data.stats || {};
        setCampaignStats({
          totalCampaigns: s.totalCampaigns ?? 0,
          totalImpressions: s.totalImpressions ?? 0,
          totalClicks: s.totalClicks ?? 0,
          totalRevenue: s.totalRevenue ?? 0,
        });
      }
    } catch (e) {
      // Non-fatal; we'll compute from list
      console.warn('Failed to load campaign stats, falling back to computed totals');
    }
  };

  useEffect(() => {
    loadCampaigns();
    loadStats();
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [selectedLocation?._id]);

  const getStatusColor = (status) => {
    switch (status) {
      case 'Active':
        return 'success';
      case 'Paused':
        return 'warning';
      case 'Completed':
        return 'primary';
      case 'Draft':
        return 'default';
      default:
        return 'default';
    }
  };

  const getCTR = (clicks, impressions) => {
    return ((clicks / impressions) * 100).toFixed(2);
  };

  const getConversionRate = (conversions, clicks) => {
    return ((conversions / clicks) * 100).toFixed(2);
  };

  const getROAS = (revenue, spent) => {
    return (revenue / spent).toFixed(2);
  };

  const handleMenuOpen = (event, campaign) => {
    setAnchorEl(event.currentTarget);
    setSelectedCampaign(campaign);
  };

  const handleMenuClose = () => {
    setAnchorEl(null);
    setSelectedCampaign(null);
  };

  const handleDetailsOpen = (campaign) => {
    setSelectedCampaign(campaign);
    setDetailsOpen(true);
    setAnchorEl(null);
  };

  return (
    <Box>
      <Box sx={{ display: 'flex', justifyContent: 'space-between', alignItems: 'center', mb: 4 }}>
        <Typography variant="h4" component="h1" sx={{ fontWeight: 'bold' }}>
          Marketing Campaigns
        </Typography>
        <Box sx={{ display: 'flex', gap: 2 }}>
          <Button
            variant="outlined"
            startIcon={<AIIcon />}
            size="large"
            onClick={() => setAiOpen(true)}
            sx={{ fontWeight: 'bold' }}
          >
            AI Campaign
          </Button>
          <Button
            variant="contained"
            startIcon={<AddIcon />}
            size="large"
            onClick={() => setCreateOpen(true)}
            sx={{ fontWeight: 'bold' }}
          >
            Create Campaign
          </Button>
        </Box>
      </Box>

      {/* Stats Cards */}
      <Grid container spacing={3} sx={{ mb: 4 }}>
        {stats.map((stat, index) => (
          <Grid item xs={12} sm={6} md={3} key={index}>
            <Card>
              <CardContent>
                <Box sx={{ display: 'flex', alignItems: 'center', justifyContent: 'space-between', mb: 2 }}>
                  {stat.icon}
                </Box>
                <Typography variant="h4" component="div" sx={{ fontWeight: 'bold', mb: 1 }}>
                  {stat.value}
                </Typography>
                <Typography variant="body2" color="text.secondary">
                  {stat.title}
                </Typography>
              </CardContent>
            </Card>
          </Grid>
        ))}
      </Grid>

      {/* Campaigns Overview Cards */}
      <Grid container spacing={3} sx={{ mb: 4 }}>
        {normalizedCampaigns.map((campaign) => (
          <Grid item xs={12} md={6} key={campaign.id}>
            <Card>
              <CardContent>
                <Box sx={{ display: 'flex', justifyContent: 'space-between', alignItems: 'flex-start', mb: 2 }}>
                  <Box sx={{ display: 'flex', alignItems: 'center' }}>
                    <Avatar sx={{ mr: 2, backgroundColor: 'primary.main' }}>
                      <CampaignIcon />
                    </Avatar>
                    <Box>
                      <Typography variant="h6" sx={{ fontWeight: 'bold' }}>
                        {campaign.name}
                      </Typography>
                      <Typography variant="body2" color="text.secondary">
                        {campaign.type}
                      </Typography>
                    </Box>
                  </Box>
                  <Box sx={{ display: 'flex', alignItems: 'center' }}>
                    <Chip
                      label={campaign.status}
                      color={getStatusColor(campaign.status)}
                      size="small"
                      sx={{ mr: 1 }}
                    />
                    <IconButton size="small" onClick={(e) => handleMenuOpen(e, campaign)}>
                      <MoreIcon />
                    </IconButton>
                  </Box>
                </Box>

                <Typography variant="body2" color="text.secondary" sx={{ mb: 2 }}>
                  {campaign.description}
                </Typography>

                {/* Budget Progress */}
                <Box sx={{ mb: 2 }}>
                  <Box sx={{ display: 'flex', justifyContent: 'space-between', mb: 1 }}>
                    <Typography variant="body2">Budget Used</Typography>
                    <Typography variant="body2">
                      ${campaign.spent.toLocaleString()} / ${campaign.budget.toLocaleString()}
                    </Typography>
                  </Box>
                  <LinearProgress
                    variant="determinate"
                    value={(campaign.budget > 0 ? (campaign.spent / campaign.budget) * 100 : 0)}
                    color={campaign.budget > 0 && campaign.spent / campaign.budget > 0.8 ? 'warning' : 'primary'}
                  />
                </Box>

                {/* Key Metrics */}
                <Grid container spacing={2}>
                  <Grid item xs={6}>
                    <Typography variant="caption" color="text.secondary">Impressions</Typography>
                    <Typography variant="body1" sx={{ fontWeight: 'bold' }}>
                      {campaign.impressions.toLocaleString()}
                    </Typography>
                  </Grid>
                  <Grid item xs={6}>
                    <Typography variant="caption" color="text.secondary">Clicks</Typography>
                    <Typography variant="body1" sx={{ fontWeight: 'bold' }}>
                      {campaign.clicks.toLocaleString()}
                    </Typography>
                  </Grid>
                  <Grid item xs={6}>
                    <Typography variant="caption" color="text.secondary">CTR</Typography>
                    <Typography variant="body1" sx={{ fontWeight: 'bold' }}>
                      {getCTR(campaign.clicks, campaign.impressions)}%
                    </Typography>
                  </Grid>
                  <Grid item xs={6}>
                    <Typography variant="caption" color="text.secondary">ROAS</Typography>
                    <Typography variant="body1" sx={{ fontWeight: 'bold' }}>
                      {getROAS(campaign.revenue, campaign.spent)}x
                    </Typography>
                  </Grid>
                </Grid>
              </CardContent>
            </Card>
          </Grid>
        ))}
      </Grid>

      {/* Detailed Performance Table */}
      <Card>
        <CardContent>
          <Typography variant="h6" sx={{ fontWeight: 'bold', mb: 2 }}>
            Campaign Performance
          </Typography>
          <TableContainer component={Paper} variant="outlined">
            <Table>
              <TableHead>
                <TableRow>
                  <TableCell sx={{ fontWeight: 'bold' }}>Campaign</TableCell>
                  <TableCell sx={{ fontWeight: 'bold' }}>Type</TableCell>
                  <TableCell sx={{ fontWeight: 'bold' }}>Impressions</TableCell>
                  <TableCell sx={{ fontWeight: 'bold' }}>Clicks</TableCell>
                  <TableCell sx={{ fontWeight: 'bold' }}>CTR</TableCell>
                  <TableCell sx={{ fontWeight: 'bold' }}>Conversions</TableCell>
                  <TableCell sx={{ fontWeight: 'bold' }}>Revenue</TableCell>
                  <TableCell sx={{ fontWeight: 'bold' }}>ROAS</TableCell>
                </TableRow>
              </TableHead>
              <TableBody>
                {normalizedCampaigns.map((campaign) => (
                  <TableRow key={campaign.id} hover>
                    <TableCell>
                      <Typography variant="body2" sx={{ fontWeight: 'medium' }}>
                        {campaign.name}
                      </Typography>
                    </TableCell>
                    <TableCell>{campaign.type}</TableCell>
                    <TableCell>{campaign.impressions.toLocaleString()}</TableCell>
                    <TableCell>{campaign.clicks.toLocaleString()}</TableCell>
                    <TableCell>{getCTR(campaign.clicks, campaign.impressions)}%</TableCell>
                    <TableCell>{campaign.conversions}</TableCell>
                    <TableCell>${campaign.revenue.toLocaleString()}</TableCell>
                    <TableCell>{getROAS(campaign.revenue, campaign.spent)}x</TableCell>
                  </TableRow>
                ))}
              </TableBody>
            </Table>
          </TableContainer>
        </CardContent>
      </Card>

      {/* Actions Menu */}
      <Menu
        anchorEl={anchorEl}
        open={Boolean(anchorEl)}
        onClose={handleMenuClose}
      >
        <MenuItem onClick={() => handleDetailsOpen(selectedCampaign)}>
          <ViewsIcon sx={{ mr: 1 }} />
          View Details
        </MenuItem>
        <MenuItem>
          <EditIcon sx={{ mr: 1 }} />
          Edit Campaign
        </MenuItem>
        <MenuItem>
          {selectedCampaign?.status === 'Active' ? (
            <>
              <PauseIcon sx={{ mr: 1 }} />
              Pause Campaign
            </>
          ) : (
            <>
              <PlayIcon sx={{ mr: 1 }} />
              Resume Campaign
            </>
          )}
        </MenuItem>
        <MenuItem>
          <DeleteIcon sx={{ mr: 1 }} />
          Delete Campaign
        </MenuItem>
      </Menu>

      {/* Campaign Details Dialog */}
      <Dialog open={detailsOpen} onClose={() => setDetailsOpen(false)} maxWidth="md" fullWidth>
        <DialogTitle>Campaign Details - {selectedCampaign?.name}</DialogTitle>
        <DialogContent>
          {selectedCampaign && (
            <Grid container spacing={3}>
              <Grid item xs={12} md={6}>
                <Typography variant="h6" sx={{ fontWeight: 'bold', mb: 2 }}>
                  Campaign Information
                </Typography>
                <Box sx={{ mb: 2 }}>
                  <Typography variant="body2" color="text.secondary">Campaign ID</Typography>
                  <Typography>{selectedCampaign.id}</Typography>
                </Box>
                <Box sx={{ mb: 2 }}>
                  <Typography variant="body2" color="text.secondary">Type</Typography>
                  <Typography>{selectedCampaign.type}</Typography>
                </Box>
                <Box sx={{ mb: 2 }}>
                  <Typography variant="body2" color="text.secondary">Status</Typography>
                  <Chip label={selectedCampaign.status} color={getStatusColor(selectedCampaign.status)} size="small" />
                </Box>
                <Box sx={{ mb: 2 }}>
                  <Typography variant="body2" color="text.secondary">Duration</Typography>
                  <Typography>{selectedCampaign.startDate} - {selectedCampaign.endDate}</Typography>
                </Box>
              </Grid>
              <Grid item xs={12} md={6}>
                <Typography variant="h6" sx={{ fontWeight: 'bold', mb: 2 }}>
                  Performance Metrics
                </Typography>
                <Box sx={{ mb: 2 }}>
                  <Typography variant="body2" color="text.secondary">Budget / Spent</Typography>
                  <Typography>${selectedCampaign.budget.toLocaleString()} / ${selectedCampaign.spent.toLocaleString()}</Typography>
                </Box>
                <Box sx={{ mb: 2 }}>
                  <Typography variant="body2" color="text.secondary">Total Revenue</Typography>
                  <Typography>${selectedCampaign.revenue.toLocaleString()}</Typography>
                </Box>
                <Box sx={{ mb: 2 }}>
                  <Typography variant="body2" color="text.secondary">Conversion Rate</Typography>
                  <Typography>{getConversionRate(selectedCampaign.conversions, selectedCampaign.clicks)}%</Typography>
                </Box>
                <Box sx={{ mb: 2 }}>
                  <Typography variant="body2" color="text.secondary">Return on Ad Spend</Typography>
                  <Typography>{getROAS(selectedCampaign.revenue, selectedCampaign.spent)}x</Typography>
                </Box>
              </Grid>
            </Grid>
          )}
        </DialogContent>
        <DialogActions>
          <Button onClick={() => setDetailsOpen(false)}>Close</Button>
          <Button variant="contained">Edit Campaign</Button>
        </DialogActions>
      </Dialog>

      {/* Create Campaign Dialog */}
      <Dialog open={createOpen} onClose={() => setCreateOpen(false)} maxWidth="sm" fullWidth>
        <DialogTitle>Create New Campaign</DialogTitle>
        <DialogContent>
          <TextField
            fullWidth
            label="Campaign Name"
            margin="normal"
          />
          <FormControl fullWidth margin="normal">
            <InputLabel>Campaign Type</InputLabel>
            <Select label="Campaign Type">
              <SelectMenuItem value="Email Marketing">Email Marketing</SelectMenuItem>
              <SelectMenuItem value="Social Media">Social Media</SelectMenuItem>
              <SelectMenuItem value="Google Ads">Google Ads</SelectMenuItem>
              <SelectMenuItem value="Display Ads">Display Ads</SelectMenuItem>
            </Select>
          </FormControl>
          <TextField
            fullWidth
            label="Budget"
            type="number"
            margin="normal"
          />
          <TextField
            fullWidth
            label="Description"
            multiline
            rows={3}
            margin="normal"
          />
        </DialogContent>
        <DialogActions>
          <Button onClick={() => setCreateOpen(false)}>Cancel</Button>
          <Button variant="contained">Create Campaign</Button>
        </DialogActions>
      </Dialog>

      {/* AI Campaign Creator Dialog */}
      <Dialog open={aiOpen} onClose={() => { setAiOpen(false); setAiSuggestion(null); setAiError(null); }} maxWidth="sm" fullWidth>
        <DialogTitle>AI Campaign Creator</DialogTitle>
        <DialogContent>
          {!aiSuggestion && (
            <>
              <TextField
                fullWidth
                label="Brief"
                placeholder="Describe your business, offer, and what you want to achieve"
                multiline
                rows={4}
                value={aiBrief}
                onChange={(e) => setAiBrief(e.target.value)}
                margin="normal"
              />
              <TextField
                fullWidth
                label="Objective"
                value={aiObjective}
                onChange={(e) => setAiObjective(e.target.value)}
                margin="normal"
              />
              <TextField
                fullWidth
                label="Audience"
                value={aiAudience}
                onChange={(e) => setAiAudience(e.target.value)}
                margin="normal"
              />
              <Grid container spacing={2}>
                <Grid item xs={6}>
                  <TextField
                    fullWidth
                    label="Budget (USD)"
                    type="number"
                    value={aiBudget}
                    onChange={(e) => setAiBudget(e.target.value)}
                    margin="normal"
                  />
                </Grid>
                <Grid item xs={6}>
                  <TextField
                    fullWidth
                    label="Duration (days)"
                    type="number"
                    value={aiDurationDays}
                    onChange={(e) => setAiDurationDays(Number(e.target.value))}
                    margin="normal"
                  />
                </Grid>
              </Grid>
              {aiError && (
                <Typography color="error" variant="body2" sx={{ mt: 1 }}>{aiError}</Typography>
              )}
            </>
          )}
          {aiSuggestion && (
            <Box>
              <Typography variant="h6" sx={{ fontWeight: 'bold', mb: 1 }}>{aiSuggestion.name}</Typography>
              <Typography variant="body2" sx={{ mb: 2 }}>{aiSuggestion.description}</Typography>
              <Grid container spacing={2}>
                <Grid item xs={6}>
                  <TextField fullWidth label="Type" value={aiSuggestion.type} InputProps={{ readOnly: true }} />
                </Grid>
                <Grid item xs={6}>
                  <TextField fullWidth label="Status" value={aiSuggestion.status} InputProps={{ readOnly: true }} />
                </Grid>
                <Grid item xs={6}>
                  <TextField fullWidth label="Start Date" value={aiSuggestion.schedule?.startDate} InputProps={{ readOnly: true }} />
                </Grid>
                <Grid item xs={6}>
                  <TextField fullWidth label="End Date" value={aiSuggestion.schedule?.endDate} InputProps={{ readOnly: true }} />
                </Grid>
                <Grid item xs={12}>
                  <TextField fullWidth label="Budget (USD)" value={aiSuggestion.budget?.total ?? 0} InputProps={{ readOnly: true }} />
                </Grid>
              </Grid>
            </Box>
          )}
        </DialogContent>
        <DialogActions>
          {!aiSuggestion ? (
            <>
              <Button onClick={() => { setAiOpen(false); setAiError(null); }}>Cancel</Button>
              <Button
                variant="contained"
                disabled={aiLoading || !selectedLocation?._id || !aiBrief || aiBrief.length < 10}
                onClick={async () => {
                  setAiError(null);
                  setAiLoading(true);
                  try {
                    const { data } = await api.post('/marketing/campaigns/ai', {
                      locationId: selectedLocation?._id,
                      brief: aiBrief,
                      objective: aiObjective || undefined,
                      audience: aiAudience || undefined,
                      budget: aiBudget ? Number(aiBudget) : undefined,
                      durationDays: aiDurationDays || 30,
                    });
                    if (data?.success) {
                      setAiSuggestion(data.data.suggestion);
                    } else {
                      setAiError(data?.message || 'Failed to generate suggestion');
                    }
                  } catch (e) {
                    setAiError(e?.response?.data?.message || 'Failed to generate suggestion');
                  } finally {
                    setAiLoading(false);
                  }
                }}
              >
                {aiLoading ? 'Generating…' : 'Generate Suggestion'}
              </Button>
            </>
          ) : (
            <>
              <Button onClick={() => { setAiSuggestion(null); }}>Back</Button>
              <Button
                variant="contained"
                onClick={async () => {
                  try {
                    const payload = {
                      name: aiSuggestion.name,
                      description: aiSuggestion.description,
                      locationId: selectedLocation?._id,
                      type: aiSuggestion.type, // backend maps to enum uppercase
                      status: 'draft',
                      schedule: {
                        startDate: aiSuggestion.schedule?.startDate,
                        endDate: aiSuggestion.schedule?.endDate,
                      },
                      budget: { total: aiSuggestion.budget?.total ?? 0 },
                    };
                    const { data } = await api.post('/marketing/campaigns', payload);
                    if (data?.success) {
                      setAiOpen(false);
                      setAiSuggestion(null);
                      setAiBrief('');
                      setAiObjective('');
                      setAiAudience('');
                      setAiBudget('');
                      setAiDurationDays(30);
                      await loadCampaigns();
                      await loadStats();
                    } else {
                      setAiError(data?.message || 'Failed to create campaign');
                    }
                  } catch (e) {
                    setAiError(e?.response?.data?.message || 'Failed to create campaign');
                  }
                }}
              >
                Create This Campaign
              </Button>
            </>
          )}
        </DialogActions>
      </Dialog>
    </Box>
  );
};

export default Campaigns;
